import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Alert,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tooltip,
    Tabs,
    Tab,
    TextField,
    IconButton,
    Stack,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import {
    CheckCircle,
    Cancel,
    Person,
    Schedule,
    AdminPanelSettings,
    Refresh,
    LockReset,
    ContentCopy,
    BadgeOutlined,
    PersonOff,
    HowToReg,
    DeleteOutline,
    Block,
} from "@mui/icons-material";
import { DataTable } from "../../components/common/DataTable";
import {
    adminService,
    User,
    AdminStats,
    PasswordResetRequestItem,
    PasswordResetRequestStatus,
} from "../../services/adminService";
import { UserRole, USER_ROLE_LABELS } from "../../types";
import { GridColDef } from "@mui/x-data-grid";
import { useAuth } from "../../contexts/AuthContext";
import { NameDialog } from "../tasks/YourNameDialog";
import { DeactivateUserDialog } from "./components/DeactivateUserDialog";
import { DeleteUserDialog } from "./components/DeleteUserDialog";

type TabValue = "pending" | "all" | "reset-requests";

const resetStatusColor: Record<
    PasswordResetRequestStatus,
    "warning" | "success" | "error" | "default"
> = {
    PENDING: "warning",
    APPROVED: "success",
    REJECTED: "error",
    COMPLETED: "success",
    EXPIRED: "default",
};

const ROLE_COLOR: Record<UserRole, "secondary" | "primary" | "info" | "default"> = {
    OWNER: "secondary",
    MANAGER: "primary",
    SHOP_ASSISTANT: "info",
    INVESTOR: "default",
};

const ALL_ROLES: UserRole[] = ["OWNER", "MANAGER", "SHOP_ASSISTANT", "INVESTOR"];

// Stat cards sit two-by-two on a phone instead of stacking four tall cards.
const STAT_BOX_SX = {
    flex: { xs: "1 1 calc(50% - 6px)", sm: "1 1 250px" },
    minWidth: { xs: 0, sm: 250 },
} as const;

const RoleChip: React.FC<{ role: UserRole }> = ({ role }) => (
    <Chip
        label={USER_ROLE_LABELS[role]}
        size="small"
        color={ROLE_COLOR[role]}
        icon={role === "OWNER" ? <AdminPanelSettings /> : <Person />}
    />
);

export const UserApproval: React.FC = () => {
    const { user: currentUser, updateUser } = useAuth();
    const theme = useTheme();
    // Phones get cards — the tables need far more width than a phone has.
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    // Whose display name is being set (shown on tasks and avatars).
    const [nameUser, setNameUser] = useState<User | null>(null);
    // Deactivate / delete dialogs, and a reactivation in progress.
    const [deactivating, setDeactivating] = useState<User | null>(null);
    const [deleting, setDeleting] = useState<User | null>(null);
    const [reactivatingId, setReactivatingId] = useState<string | null>(null);

    const labelOf = (u: Pick<User, "display_name" | "email">) => u.display_name || u.email;

    const handleReactivate = async (user: User) => {
        setReactivatingId(user.user_id);
        try {
            await adminService.reactivateUser(user.user_id);
            setSuccess(`${labelOf(user)} can sign in again.`);
            await loadData();
        } catch (err: any) {
            setError(err.response?.data?.detail || "Failed to reactivate");
        } finally {
            setReactivatingId(null);
        }
    };
    const [pendingUsers, setPendingUsers] = useState<User[]>([]);
    const [allUsers, setAllUsers] = useState<User[]>([]);
    const [resetRequests, setResetRequests] = useState<
        PasswordResetRequestItem[]
    >([]);
    const [stats, setStats] = useState<AdminStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>("");
    const [success, setSuccess] = useState<string>("");
    const [activeTab, setActiveTab] = useState<TabValue>("pending");
    const [confirmDialog, setConfirmDialog] = useState<{
        open: boolean;
        action: "approve" | "reject";
        user: User | null;
    }>({ open: false, action: "approve", user: null });

    // Reviewing a self-service reset request (approve/reject, optional note)
    const [reviewDialog, setReviewDialog] = useState<{
        open: boolean;
        action: "approve" | "reject";
        request: PasswordResetRequestItem | null;
        note: string;
        submitting: boolean;
    }>({ open: false, action: "approve", request: null, note: "", submitting: false });

    // Starting a reset directly from the Users list, no prior request —
    // the note is required here (see design doc's "reason is required for
    // the direct path" safeguard).
    const [directResetDialog, setDirectResetDialog] = useState<{
        open: boolean;
        user: User | null;
        note: string;
        submitting: boolean;
    }>({ open: false, user: null, note: "", submitting: false });

    const [roleDialog, setRoleDialog] = useState<{
        open: boolean;
        user: User | null;
        newRole: UserRole;
        submitting: boolean;
    }>({ open: false, user: null, newRole: "SHOP_ASSISTANT", submitting: false });

    // Shown once after an approve or a direct reset succeeds — the raw
    // token never persists past this response, so this is the only chance
    // to copy the link.
    const [revealLink, setRevealLink] = useState<{
        open: boolean;
        resetToken: string;
        expiresAt: string;
        forEmail: string;
    }>({ open: false, resetToken: "", expiresAt: "", forEmail: "" });
    const [linkCopied, setLinkCopied] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            setError("");

            const [pendingData, allUsersData, statsData, resetRequestsData] =
                await Promise.all([
                    adminService.getPendingUsers(),
                    adminService.getAllUsers(),
                    adminService.getAdminStats(),
                    adminService.getPasswordResetRequests(),
                ]);

            setPendingUsers(pendingData);
            setAllUsers(allUsersData);
            setStats(statsData);
            setResetRequests(resetRequestsData.items);
        } catch (err: any) {
            console.error("Admin data loading error:", err);
            setError(err.response?.data?.detail || "Failed to load data");
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (user: User) => {
        try {
            setError("");
            await adminService.approveUser(user.user_id);
            setSuccess(`User ${user.email} approved successfully`);
            await loadData();
            setConfirmDialog({ open: false, action: "approve", user: null });
        } catch (err: any) {
            setError(err.response?.data?.detail || "Failed to approve user");
        }
    };

    const handleReject = async (user: User) => {
        try {
            setError("");
            await adminService.rejectUser(user.user_id);
            setSuccess(`User ${user.email} rejected and removed`);
            await loadData();
            setConfirmDialog({ open: false, action: "reject", user: null });
        } catch (err: any) {
            setError(err.response?.data?.detail || "Failed to reject user");
        }
    };

    const openConfirmDialog = (action: "approve" | "reject", user: User) => {
        setConfirmDialog({ open: true, action, user });
    };

    const openReviewDialog = (
        action: "approve" | "reject",
        request: PasswordResetRequestItem,
    ) => {
        setReviewDialog({ open: true, action, request, note: "", submitting: false });
    };

    const closeReviewDialog = () => {
        setReviewDialog({ open: false, action: "approve", request: null, note: "", submitting: false });
    };

    const handleSubmitReview = async () => {
        if (!reviewDialog.request) return;
        try {
            setReviewDialog((prev) => ({ ...prev, submitting: true }));
            setError("");

            if (reviewDialog.action === "approve") {
                const approval = await adminService.approvePasswordResetRequest(
                    reviewDialog.request.request_id,
                    reviewDialog.note || undefined,
                );
                closeReviewDialog();
                setRevealLink({
                    open: true,
                    resetToken: approval.reset_token,
                    expiresAt: approval.expires_at,
                    forEmail: reviewDialog.request.user_email || "this user",
                });
            } else {
                await adminService.rejectPasswordResetRequest(
                    reviewDialog.request.request_id,
                    reviewDialog.note || undefined,
                );
                setSuccess(`Reset request for ${reviewDialog.request.user_email} rejected`);
                closeReviewDialog();
            }
            await loadData();
        } catch (err: any) {
            setError(err.response?.data?.detail || "Failed to update the request");
            setReviewDialog((prev) => ({ ...prev, submitting: false }));
        }
    };

    const openDirectResetDialog = (user: User) => {
        setDirectResetDialog({ open: true, user, note: "", submitting: false });
    };

    const closeDirectResetDialog = () => {
        setDirectResetDialog({ open: false, user: null, note: "", submitting: false });
    };

    const handleSubmitDirectReset = async () => {
        if (!directResetDialog.user || !directResetDialog.note.trim()) return;
        try {
            setDirectResetDialog((prev) => ({ ...prev, submitting: true }));
            setError("");
            const approval = await adminService.directResetUserPassword(
                directResetDialog.user.user_id,
                directResetDialog.note.trim(),
            );
            const forEmail = directResetDialog.user.email;
            closeDirectResetDialog();
            setRevealLink({
                open: true,
                resetToken: approval.reset_token,
                expiresAt: approval.expires_at,
                forEmail,
            });
            await loadData();
        } catch (err: any) {
            setError(err.response?.data?.detail || "Failed to reset the password");
            setDirectResetDialog((prev) => ({ ...prev, submitting: false }));
        }
    };

    const openRoleDialog = (user: User) => {
        setRoleDialog({ open: true, user, newRole: user.role, submitting: false });
    };

    const closeRoleDialog = () => {
        setRoleDialog({ open: false, user: null, newRole: "SHOP_ASSISTANT", submitting: false });
    };

    const handleSubmitRoleChange = async () => {
        if (!roleDialog.user) return;
        try {
            setRoleDialog((prev) => ({ ...prev, submitting: true }));
            setError("");
            await adminService.setUserRole(roleDialog.user.user_id, roleDialog.newRole);
            setSuccess(`${roleDialog.user.email}'s role set to ${USER_ROLE_LABELS[roleDialog.newRole]}`);
            closeRoleDialog();
            await loadData();
        } catch (err: any) {
            setError(err.response?.data?.detail || "Failed to change role");
            setRoleDialog((prev) => ({ ...prev, submitting: false }));
        }
    };

    const resetLink = revealLink.resetToken
        ? `${window.location.origin}/reset-password?token=${revealLink.resetToken}`
        : "";

    const handleCopyResetLink = () => {
        navigator.clipboard.writeText(resetLink);
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
    };

    const closeRevealLink = () => {
        setRevealLink({ open: false, resetToken: "", expiresAt: "", forEmail: "" });
        setLinkCopied(false);
    };

    // ── Pieces shared by the tables (desktop) and the cards (phone) ─────────

    const registeredLine = (user: Pick<User, "create_time">) => (
        <Typography variant="caption" color="text.secondary">
            Registered: {new Date(user.create_time).toLocaleDateString()}
        </Typography>
    );

    const deactivationText = (user: User) => {
        const by = allUsers.find((u) => u.user_id === user.deactivated_by_user_id);
        const when = user.deactivated_at ? new Date(user.deactivated_at).toLocaleDateString() : "";
        return [
            `Deactivated${when ? ` on ${when}` : ""}${by ? ` by ${labelOf(by)}` : ""}`,
            user.deactivation_note ? `“${user.deactivation_note}”` : null,
        ]
            .filter(Boolean)
            .join(" — ");
    };

    const accountStatusChip = (user: User) => {
        if (user.is_active === false) {
            return (
                <Tooltip title={deactivationText(user)}>
                    <Chip label="Deactivated" size="small" icon={<Block />} variant="outlined" />
                </Tooltip>
            );
        }
        return (
            <Chip
                label={user.is_approved ? "Active" : "Pending"}
                size="small"
                color={user.is_approved ? "success" : "warning"}
                icon={user.is_approved ? <CheckCircle /> : <Schedule />}
            />
        );
    };

    const pendingActions = (user: User) => (
        <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title="Approve User">
                <Button
                    size="small"
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircle />}
                    onClick={() => openConfirmDialog("approve", user)}
                >
                    Approve
                </Button>
            </Tooltip>
            <Tooltip title="Reject User">
                <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<Cancel />}
                    onClick={() => openConfirmDialog("reject", user)}
                >
                    Reject
                </Button>
            </Tooltip>
        </Box>
    );

    /** `wrap` lets the buttons flow onto a second line (cards only — a
     * table row has a fixed height). */
    const accountActions = (row: User, wrap = false) => {
        const isMe = row.user_id === currentUser?.user_id;
        const isOwner = row.role === "OWNER";
        const inactive = row.is_active === false;
        // Owners must be demoted first; nobody can switch themselves off.
        const blockedWhy = isMe ? "You can't do this to your own account" : isOwner ? "Change their role first — Owners can't be deactivated or deleted" : "";
        return (
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: wrap ? "wrap" : "nowrap" }}>
                {inactive ? (
                    <Tooltip title="Let them sign in again">
                        <span>
                            <Button
                                size="small"
                                variant="contained"
                                color="success"
                                startIcon={<HowToReg />}
                                disabled={reactivatingId === row.user_id}
                                onClick={() => handleReactivate(row)}
                            >
                                Reactivate
                            </Button>
                        </span>
                    </Tooltip>
                ) : (
                    <Tooltip title={blockedWhy || "Stop them signing in — keeps everything they did"}>
                        <span>
                            <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                startIcon={<PersonOff />}
                                disabled={!!blockedWhy}
                                onClick={() => setDeactivating(row)}
                            >
                                Deactivate
                            </Button>
                        </span>
                    </Tooltip>
                )}
                <Tooltip title={blockedWhy || "Delete permanently (only if they have no history)"}>
                    <span>
                        <IconButton size="small" color="error" disabled={!!blockedWhy} onClick={() => setDeleting(row)} aria-label="Delete permanently">
                            <DeleteOutline fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="The name shown on tasks and avatars">
                    <Button size="small" variant="outlined" startIcon={<BadgeOutlined />} onClick={() => setNameUser(row)}>
                        Name
                    </Button>
                </Tooltip>
                <Tooltip title="Change this user's role">
                    <Button size="small" variant="outlined" startIcon={<AdminPanelSettings />} onClick={() => openRoleDialog(row)}>
                        Role
                    </Button>
                </Tooltip>
                {row.is_approved && (
                    <Tooltip title="Reset this user's password — no request needed">
                        <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            startIcon={<LockReset />}
                            onClick={() => openDirectResetDialog(row)}
                        >
                            Reset
                        </Button>
                    </Tooltip>
                )}
            </Box>
        );
    };

    const resetRequestActions = (request: PasswordResetRequestItem) =>
        request.status === "PENDING" ? (
            <Box sx={{ display: "flex", gap: 1 }}>
                <Tooltip title="Approve — I've verified it's them">
                    <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircle />}
                        onClick={() => openReviewDialog("approve", request)}
                    >
                        Approve
                    </Button>
                </Tooltip>
                <Tooltip title="Reject">
                    <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<Cancel />}
                        onClick={() => openReviewDialog("reject", request)}
                    >
                        Reject
                    </Button>
                </Tooltip>
            </Box>
        ) : null;

    const resetOrigin = (request: PasswordResetRequestItem) => (
        <Chip label={request.initiated_by === "ADMIN" ? "Admin" : "Self-service"} size="small" variant="outlined" />
    );

    const resetStatus = (request: PasswordResetRequestItem) => (
        <Chip label={request.status} size="small" color={resetStatusColor[request.status]} />
    );

    const columns: GridColDef[] = [
        {
            field: "email",
            headerName: "Email",
            flex: 1,
            minWidth: 250,
            renderCell: (params) => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Person color="primary" />
                    <Box>
                        <Typography variant="body2" fontWeight="medium">
                            {params.value}
                        </Typography>
                        {registeredLine(params.row)}
                    </Box>
                </Box>
            ),
        },
        {
            field: "role",
            headerName: "Role",
            width: 140,
            renderCell: (params) => <RoleChip role={params.value as UserRole} />,
        },
        {
            field: "status",
            headerName: "Status",
            width: 120,
            renderCell: () => (
                <Chip
                    label="Pending"
                    size="small"
                    color="warning"
                    icon={<Schedule />}
                />
            ),
        },
        {
            field: "userActions",
            headerName: "Actions",
            width: 200,
            sortable: false,
            renderCell: (params) => pendingActions(params.row as User),
        },
    ];

    // Columns for all users table
    const allUsersColumns: GridColDef[] = [
        {
            field: "email",
            headerName: "Email",
            flex: 1,
            minWidth: 250,
            renderCell: (params) => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, opacity: params.row.is_active === false ? 0.55 : 1 }}>
                    {params.row.is_active === false ? <PersonOff color="disabled" /> : <Person color="primary" />}
                    <Box>
                        <Typography variant="body2" fontWeight="medium">
                            {params.row.display_name ? `${params.row.display_name} · ` : ""}
                            {params.value}
                        </Typography>
                        {registeredLine(params.row)}
                    </Box>
                </Box>
            ),
        },
        {
            field: "role",
            headerName: "Role",
            width: 140,
            renderCell: (params) => <RoleChip role={params.value as UserRole} />,
        },
        {
            field: "is_approved",
            headerName: "Status",
            width: 130,
            renderCell: (params) => accountStatusChip(params.row as User),
        },
        {
            field: "userAccountActions",
            headerName: "Actions",
            width: 500,
            sortable: false,
            renderCell: (params) => accountActions(params.row as User),
        },
    ];

    const resetRequestColumns: GridColDef[] = [
        {
            field: "user_email",
            headerName: "Requested By",
            flex: 1,
            minWidth: 220,
            renderCell: (params) => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Person color="primary" />
                    <Box>
                        <Typography variant="body2" fontWeight="medium">
                            {params.value || "Unknown"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {new Date(params.row.requested_at).toLocaleString()}
                        </Typography>
                    </Box>
                </Box>
            ),
        },
        {
            field: "initiated_by",
            headerName: "Started By",
            width: 140,
            renderCell: (params) => resetOrigin(params.row),
        },
        {
            field: "status",
            headerName: "Status",
            width: 130,
            renderCell: (params) => resetStatus(params.row),
        },
        {
            field: "review_note",
            headerName: "Note",
            flex: 1,
            minWidth: 200,
            renderCell: (params) => (
                <Typography variant="body2" color="text.secondary" noWrap>
                    {params.value || "—"}
                </Typography>
            ),
        },
        {
            field: "requestActions",
            headerName: "Actions",
            width: 200,
            sortable: false,
            renderCell: (params) => resetRequestActions(params.row),
        },
    ];

    // ── Phone cards ─────────────────────────────────────────────────────────

    const cardShell = (key: string, children: React.ReactNode, dim = false) => (
        <Card key={key} variant="outlined" sx={{ mb: 1.5, borderRadius: 2, opacity: dim ? 0.7 : 1 }}>
            <CardContent sx={{ p: 1.75, "&:last-child": { pb: 1.75 } }}>
                <Stack spacing={1.25}>{children}</Stack>
            </CardContent>
        </Card>
    );

    const personHeader = (icon: React.ReactNode, title: React.ReactNode, sub: React.ReactNode) => (
        <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
            <Box sx={{ pt: 0.25, display: "flex" }}>{icon}</Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} sx={{ wordBreak: "break-word" }}>
                    {title}
                </Typography>
                {sub}
            </Box>
        </Box>
    );

    const pendingCards = () =>
        pendingUsers.map((user) =>
            cardShell(
                user.user_id,
                <>
                    {personHeader(<Person color="primary" />, user.email, registeredLine(user))}
                    <Stack direction="row" spacing={1}>
                        <RoleChip role={user.role} />
                        <Chip label="Pending" size="small" color="warning" icon={<Schedule />} />
                    </Stack>
                    {pendingActions(user)}
                </>,
            ),
        );

    const allUserCards = () =>
        allUsersRows.map((user) => {
            const inactive = user.is_active === false;
            return cardShell(
                user.user_id,
                <>
                    {personHeader(
                        inactive ? <PersonOff color="disabled" /> : <Person color="primary" />,
                        <>
                            {user.display_name && <Box component="span" sx={{ display: "block" }}>{user.display_name}</Box>}
                            <Box component="span" sx={{ fontWeight: user.display_name ? 400 : 600, color: user.display_name ? "text.secondary" : "inherit" }}>
                                {user.email}
                            </Box>
                        </>,
                        registeredLine(user),
                    )}
                    <Stack direction="row" spacing={1}>
                        <RoleChip role={user.role} />
                        {accountStatusChip(user)}
                    </Stack>
                    {/* Tooltips need a long-press on a phone, so spell it out. */}
                    {inactive && (
                        <Typography variant="caption" color="text.secondary">
                            {deactivationText(user)}
                        </Typography>
                    )}
                    {accountActions(user, true)}
                </>,
                inactive,
            );
        });

    const resetRequestCards = () =>
        resetRequests.map((request) =>
            cardShell(
                request.request_id,
                <>
                    {personHeader(
                        <Person color="primary" />,
                        request.user_email || "Unknown",
                        <Typography variant="caption" color="text.secondary">
                            {new Date(request.requested_at).toLocaleString()}
                        </Typography>,
                    )}
                    <Stack direction="row" spacing={1}>
                        {resetOrigin(request)}
                        {resetStatus(request)}
                    </Stack>
                    {request.review_note && (
                        <Typography variant="body2" color="text.secondary">
                            {request.review_note}
                        </Typography>
                    )}
                    {resetRequestActions(request)}
                </>,
            ),
        );

    const pendingRows = pendingUsers.map((user) => ({
        id: user.user_id,
        ...user,
    }));

    // Deactivated accounts last.
    const allUsersRows = [...allUsers]
        .sort((a, b) => Number(a.is_active === false) - Number(b.is_active === false))
        .map((user) => ({
            id: user.user_id,
            ...user,
        }));

    const resetRequestRows = resetRequests.map((request) => ({
        id: request.request_id,
        ...request,
    }));

    return (
        <Box sx={{ p: { xs: 0, sm: 3 } }}>
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1,
                    mb: 3,
                }}
            >
                <Typography variant="h4" component="h1" sx={{ fontSize: { xs: "1.75rem", sm: "2.125rem" } }}>
                    Staff Accounts
                </Typography>
                <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={loadData}
                    disabled={loading}
                >
                    Refresh
                </Button>
            </Box>

            {/* Stats Cards */}
            {stats && (
                <Box sx={{ display: "flex", gap: { xs: 1.5, sm: 3 }, mb: 3, flexWrap: "wrap" }}>
                    <Box sx={STAT_BOX_SX}>
                        <Card>
                            <CardContent>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    <Schedule color="warning" />
                                    <Box>
                                        <Typography
                                            variant="h6"
                                            color="warning.main"
                                        >
                                            {stats.pending_users}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            Pending Approval
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>
                    <Box sx={STAT_BOX_SX}>
                        <Card>
                            <CardContent>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    <CheckCircle color="success" />
                                    <Box>
                                        <Typography
                                            variant="h6"
                                            color="success.main"
                                        >
                                            {stats.approved_users}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            Approved Users
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>
                    <Box sx={STAT_BOX_SX}>
                        <Card>
                            <CardContent>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    <Person color="primary" />
                                    <Box>
                                        <Typography variant="h6">
                                            {stats.total_users}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            Total Users
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>
                    <Box sx={STAT_BOX_SX}>
                        <Card>
                            <CardContent>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    <AdminPanelSettings color="secondary" />
                                    <Box>
                                        <Typography
                                            variant="h6"
                                            color="secondary.main"
                                        >
                                            {stats.owners}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            Owners
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>
                </Box>
            )}

            {/* Alerts */}
            {currentUser && currentUser.role !== "OWNER" && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    You need Owner privileges to manage user approvals.
                    Current role: {USER_ROLE_LABELS[currentUser.role]}
                </Alert>
            )}

            {error && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    onClose={() => setError("")}
                >
                    {error}
                </Alert>
            )}

            {success && (
                <Alert
                    severity="success"
                    sx={{ mb: 2 }}
                    onClose={() => setSuccess("")}
                >
                    {success}
                </Alert>
            )}

            {/* Tabs for different lists */}
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
                <Tabs
                    value={activeTab}
                    onChange={(_, newValue) =>
                        setActiveTab(newValue as TabValue)
                    }
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    <Tab
                        label={`Pending Approvals (${pendingUsers.length})`}
                        value="pending"
                    />
                    <Tab label={`All Users (${allUsers.length})`} value="all" />
                    <Tab
                        label={`Password Reset Requests (${resetRequests.filter((r) => r.status === "PENDING").length})`}
                        value="reset-requests"
                    />
                </Tabs>
            </Box>

            {(() => {
                const tabConfig = {
                    pending: {
                        rows: pendingRows,
                        columns,
                        title: `Pending User Approvals (${pendingUsers.length})`,
                        emptyTitle: "No Pending Approvals",
                        emptyBody: "All users have been reviewed and approved.",
                        cards: pendingCards,
                    },
                    all: {
                        rows: allUsersRows,
                        columns: allUsersColumns,
                        title: `All Users (${allUsers.length})`,
                        emptyTitle: "No Users Found",
                        emptyBody: "No users found in the system.",
                        cards: allUserCards,
                    },
                    "reset-requests": {
                        rows: resetRequestRows,
                        columns: resetRequestColumns,
                        title: `Password Reset Requests (${resetRequests.length})`,
                        emptyTitle: "No Reset Requests",
                        emptyBody:
                            "No one has requested a password reset yet.",
                        cards: resetRequestCards,
                    },
                }[activeTab];

                // On a phone the list sits straight on the page as cards.
                return (
                    <Card sx={isMobile ? { bgcolor: "transparent", boxShadow: "none", overflow: "visible" } : undefined}>
                        <CardContent sx={isMobile ? { p: 0, "&:last-child": { pb: 0 } } : undefined}>
                            <Typography variant="h6" gutterBottom sx={{ display: { xs: "none", sm: "block" } }}>
                                {tabConfig.title}
                            </Typography>

                            {tabConfig.rows.length === 0 && !loading ? (
                                <Box sx={{ textAlign: "center", py: 4 }}>
                                    <CheckCircle
                                        sx={{
                                            fontSize: 64,
                                            color: "success.main",
                                            mb: 2,
                                        }}
                                    />
                                    <Typography variant="h6" color="success.main">
                                        {tabConfig.emptyTitle}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {tabConfig.emptyBody}
                                    </Typography>
                                </Box>
                            ) : isMobile ? (
                                tabConfig.cards()
                            ) : (
                                <DataTable
                                    rows={tabConfig.rows}
                                    columns={tabConfig.columns}
                                    loading={loading}
                                    pageSize={10}
                                    disableSelectionOnClick
                                    hideActions
                                />
                            )}
                        </CardContent>
                    </Card>
                );
            })()}

            {/* Confirmation Dialog */}
            <Dialog
                open={confirmDialog.open}
                onClose={() =>
                    setConfirmDialog({
                        open: false,
                        action: "approve",
                        user: null,
                    })
                }
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {confirmDialog.action === "approve"
                        ? "Approve User"
                        : "Reject User"}
                </DialogTitle>
                <DialogContent>
                    {confirmDialog.user && (
                        <Box>
                            <Typography variant="body1" gutterBottom>
                                Are you sure you want to {confirmDialog.action}{" "}
                                the following user?
                            </Typography>
                            <Box
                                sx={{
                                    mt: 2,
                                    p: 2,
                                    bgcolor: "grey.100",
                                    borderRadius: 1,
                                }}
                            >
                                <Typography variant="subtitle2">
                                    Email: {confirmDialog.user.email}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Role: {confirmDialog.user.role}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Registered:{" "}
                                    {new Date(
                                        confirmDialog.user.create_time,
                                    ).toLocaleString()}
                                </Typography>
                            </Box>
                            {confirmDialog.action === "reject" && (
                                <Alert severity="warning" sx={{ mt: 2 }}>
                                    This action will permanently delete the user
                                    account and cannot be undone.
                                </Alert>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() =>
                            setConfirmDialog({
                                open: false,
                                action: "approve",
                                user: null,
                            })
                        }
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => {
                            if (confirmDialog.user) {
                                if (confirmDialog.action === "approve") {
                                    handleApprove(confirmDialog.user);
                                } else {
                                    handleReject(confirmDialog.user);
                                }
                            }
                        }}
                        variant="contained"
                        color={
                            confirmDialog.action === "approve"
                                ? "success"
                                : "error"
                        }
                    >
                        {confirmDialog.action === "approve"
                            ? "Approve"
                            : "Reject"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Review a self-service password reset request */}
            <Dialog
                open={reviewDialog.open}
                onClose={closeReviewDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {reviewDialog.action === "approve"
                        ? "Approve Reset Request"
                        : "Reject Reset Request"}
                </DialogTitle>
                <DialogContent>
                    {reviewDialog.request && (
                        <Box>
                            <Typography variant="body1" gutterBottom>
                                {reviewDialog.action === "approve"
                                    ? "Only approve after you've confirmed it's really them — by phone or in person."
                                    : "Are you sure you want to reject this request?"}
                            </Typography>
                            <Box
                                sx={{
                                    mt: 1,
                                    mb: 2,
                                    p: 2,
                                    bgcolor: "grey.100",
                                    borderRadius: 1,
                                }}
                            >
                                <Typography variant="subtitle2">
                                    {reviewDialog.request.user_email}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Requested{" "}
                                    {new Date(
                                        reviewDialog.request.requested_at,
                                    ).toLocaleString()}
                                </Typography>
                            </Box>
                            <TextField
                                fullWidth
                                multiline
                                minRows={2}
                                label="Note (optional)"
                                placeholder="e.g. verified via call to their number on file"
                                value={reviewDialog.note}
                                onChange={(e) =>
                                    setReviewDialog((prev) => ({
                                        ...prev,
                                        note: e.target.value,
                                    }))
                                }
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeReviewDialog}>Cancel</Button>
                    <Button
                        onClick={handleSubmitReview}
                        variant="contained"
                        color={reviewDialog.action === "approve" ? "success" : "error"}
                        disabled={reviewDialog.submitting}
                    >
                        {reviewDialog.action === "approve" ? "Approve" : "Reject"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Start a reset directly from the Users list — no prior request */}
            <Dialog
                open={directResetDialog.open}
                onClose={closeDirectResetDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Reset Password</DialogTitle>
                <DialogContent>
                    {directResetDialog.user && (
                        <Box>
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                This resets {directResetDialog.user.email}'s
                                password without them asking first. Only do
                                this after confirming who you're helping.
                            </Alert>
                            <TextField
                                fullWidth
                                required
                                multiline
                                minRows={2}
                                label="Reason"
                                placeholder="e.g. locked out at the counter, verified in person"
                                value={directResetDialog.note}
                                onChange={(e) =>
                                    setDirectResetDialog((prev) => ({
                                        ...prev,
                                        note: e.target.value,
                                    }))
                                }
                                error={directResetDialog.note.trim() === ""}
                                helperText="A reason is required for a direct reset — it's kept on the audit trail."
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDirectResetDialog}>Cancel</Button>
                    <Button
                        onClick={handleSubmitDirectReset}
                        variant="contained"
                        color="warning"
                        disabled={
                            directResetDialog.submitting ||
                            directResetDialog.note.trim() === ""
                        }
                    >
                        Generate Reset Link
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Change a user's role */}
            <Dialog
                open={roleDialog.open}
                onClose={closeRoleDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Change Role</DialogTitle>
                <DialogContent>
                    {roleDialog.user && (
                        <Box>
                            <Typography variant="body1" gutterBottom>
                                {roleDialog.user.email}
                            </Typography>
                            <TextField
                                select
                                fullWidth
                                label="Role"
                                value={roleDialog.newRole}
                                onChange={(e) =>
                                    setRoleDialog((prev) => ({
                                        ...prev,
                                        newRole: e.target.value as UserRole,
                                    }))
                                }
                                sx={{ mt: 1 }}
                                SelectProps={{ native: true }}
                            >
                                {ALL_ROLES.map((role) => (
                                    <option key={role} value={role}>
                                        {USER_ROLE_LABELS[role]}
                                    </option>
                                ))}
                            </TextField>
                            {roleDialog.newRole === "OWNER" && (
                                <Alert severity="warning" sx={{ mt: 2 }}>
                                    Owners can manage every other account,
                                    including other Owners.
                                </Alert>
                            )}
                            {roleDialog.newRole === "INVESTOR" && (
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    Investor accounts get read-only access to
                                    Financial Reports, Orders & Customers, and
                                    Inventory & Stock — nothing else.
                                </Alert>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeRoleDialog}>Cancel</Button>
                    <Button
                        onClick={handleSubmitRoleChange}
                        variant="contained"
                        disabled={
                            roleDialog.submitting ||
                            roleDialog.newRole === roleDialog.user?.role
                        }
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* One-time reveal of the reset link — shown once, never again */}
            <Dialog
                open={revealLink.open}
                onClose={closeRevealLink}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Reset Link Ready</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Copy this now and send it to {revealLink.forEmail}{" "}
                        yourself — by phone, in person, however you just
                        verified them. It will not be shown again, and
                        expires{" "}
                        {revealLink.expiresAt &&
                            new Date(revealLink.expiresAt).toLocaleTimeString()}
                        .
                    </Alert>
                    <TextField
                        fullWidth
                        value={resetLink}
                        InputProps={{
                            readOnly: true,
                            endAdornment: (
                                <Tooltip title={linkCopied ? "Copied!" : "Copy"}>
                                    <IconButton onClick={handleCopyResetLink}>
                                        <ContentCopy fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            ),
                        }}
                        onClick={(e) =>
                            (e.target as HTMLInputElement).select?.()
                        }
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeRevealLink} variant="contained">
                        Done
                    </Button>
                </DialogActions>
            </Dialog>

            <DeactivateUserDialog
                user={deactivating}
                onClose={() => setDeactivating(null)}
                onDone={(user, moved) => {
                    setSuccess(
                        `${labelOf(user)} is deactivated${moved ? ` — ${moved} open task${moved === 1 ? "" : "s"} handed on` : ""}.`,
                    );
                    loadData();
                }}
            />
            <DeleteUserDialog
                user={deleting}
                onClose={() => setDeleting(null)}
                onDeleted={(user) => {
                    setSuccess(`${user.email} was deleted.`);
                    loadData();
                }}
                onDeactivateInstead={(user) => setDeactivating(user)}
            />

            <NameDialog
                open={!!nameUser}
                onClose={() => setNameUser(null)}
                user={nameUser}
                self={nameUser?.user_id === currentUser?.user_id}
                onSaved={(saved) => {
                    setAllUsers((prev) => prev.map((u) => (u.user_id === saved.user_id ? { ...u, display_name: saved.display_name } : u)));
                    if (currentUser && saved.user_id === currentUser.user_id) {
                        updateUser({ ...currentUser, display_name: saved.display_name });
                    }
                    setSuccess(saved.display_name ? `Name set to ${saved.display_name}` : "Name cleared");
                }}
            />
        </Box>
    );
};
